import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { request } from '@/lib/http';
import { ApiError } from '@/features/auth/api';
import { BADGE_COUNTS_QUERY_KEY } from '@/features/invoices/badges/queryKeys';
import { INVOICE_DRAFTS_QUERY_KEY } from '@/features/invoices/compose/api';
import { useRefreshList } from '@/features/invoices/useRefreshList';

/**
 * Outbound (issued) invoice API client. Mirrors the Ktor `/api/v1/outbound-invoices` contract 1:1 (see
 * `apps/api/.../routes/outbound/model/OutboundResponse.kt`) plus the issue entry point on the draft
 * route (`POST /api/v1/invoice-drafts/{id}/issue`). The whole surface is dark unless `me.issuingEnabled`
 * — when issuing is off every path 404s, so callers must gate on that flag (routes + nav already do).
 *
 * Money is an exact decimal string (grosz-exact `BigDecimal.toPlainString`), never `Double`.
 */

// --- Enums (server enum names on the wire) -------------------------------------------------------

/**
 * The issuance state machine, source of truth for a row's lifecycle. Terminal = COMPLETED | ABANDONED;
 * REJECTED and NEEDS_ATTENTION rest awaiting a user action; the rest are in-flight (the worker/sweep
 * advances them). See the operator runbook for the per-state meaning.
 */
export type IssuanceState =
  | 'QUEUED'
  | 'SUBMITTING'
  | 'AWAITING_KSEF'
  | 'ACCEPTED'
  | 'COMPLETED'
  | 'RETRY_WAIT'
  | 'NEEDS_ATTENTION'
  | 'REJECTED'
  | 'ABANDONED';

/** States the background pipeline is still moving on its own — the detail view polls while in one. */
const IN_FLIGHT_STATES: ReadonlySet<IssuanceState> = new Set([
  'QUEUED',
  'SUBMITTING',
  'AWAITING_KSEF',
  'ACCEPTED',
  'RETRY_WAIT',
]);

export const isInFlight = (state: IssuanceState): boolean => IN_FLIGHT_STATES.has(state);

/** Whether the invoice has reached a legally-issued state (ksefNumber assigned). */
export const isIssued = (state: IssuanceState): boolean =>
  state === 'ACCEPTED' || state === 'COMPLETED';

/** How the invoice number was assigned: auto-sequenced or a manual override at issue time. */
export type NumberSource = 'AUTO' | 'MANUAL';

// --- Responses -----------------------------------------------------------------------------------

/** A list/detail row of an outbound invoice, mirroring `OutboundInvoiceResponse`. */
export interface OutboundInvoice {
  id: string;
  invoiceNumber: string;
  numberSource: NumberSource;
  documentKind: string;
  /** How the tax was accounted for; a margin invoice is a STANDARD kind that discloses no VAT. */
  taxScheme: string;
  state: IssuanceState;
  ksefNumber: string | null;
  issueDate: string;
  acquisitionDate: string | null;
  buyerName: string;
  buyerNip: string | null;
  netTotal: string;
  vatTotal: string;
  grossTotal: string;
  currency: string;
  needsAttentionReason: string | null;
  createdAt: string;
  updatedAt: string;
}

/** One audit-timeline entry (a recorded state transition), mirroring `OutboundEventResponse`. */
export interface OutboundEvent {
  occurredAt: string;
  actor: string;
  fromState: IssuanceState | null;
  toState: IssuanceState;
  detailJson: string | null;
}

/** One submission attempt's summary, mirroring `OutboundSubmissionResponse`. */
export interface OutboundSubmission {
  attemptNo: number;
  invoiceNumber: string;
  state: string;
  rejectionCode: number | null;
  rejectionDetail: string | null;
  hasUpo: boolean;
  createdAt: string;
}

/** An invoice with its timeline + attempts, mirroring `OutboundInvoiceDetailResponse`. */
export interface OutboundInvoiceDetail {
  invoice: OutboundInvoice;
  timeline: OutboundEvent[];
  attempts: OutboundSubmission[];
}

/** A page of the outbound list, mirroring `PagedOutboundInvoices`. `nextOffset` is null on the last page. */
export interface PagedOutboundInvoices {
  items: OutboundInvoice[];
  nextOffset: number | null;
}

/** The 202 resend-accepted body, mirroring `OutboundResendResponse`. */
export interface OutboundResend {
  id: string;
  state: IssuanceState;
  invoiceNumber: string;
}

/**
 * The issue-click body, mirroring `IssueRequest`. `outboundId` is a client-generated idempotency UUID —
 * a repeat POST with the same id replays the first result. `acknowledgedWarnings` echoes the rule-warning
 * ids the user confirmed in the dialog; issue is refused until every warning is acknowledged. `number`
 * is left unset here — the composer flushes the draft first, so the persisted content already carries any
 * manual number override.
 */
export interface IssueRequest {
  outboundId: string;
  acknowledgedWarnings: string[];
}

/** The 202 issue-accepted body, mirroring `IssueAcceptedResponse`. */
export interface IssueAccepted {
  outboundId: string;
  state: IssuanceState;
}

// --- Download URLs (served as raw bytes, so linked directly) -------------------------------------

/** The accepted FA(3) XML (404 before acceptance). */
export const outboundXmlUrl = (id: string): string => `/api/v1/outbound-invoices/${id}/xml`;

/** The stored UPO (official acceptance proof; 404 until it has been fetched). */
export const outboundUpoUrl = (id: string): string => `/api/v1/outbound-invoices/${id}/upo`;

// --- Query keys ----------------------------------------------------------------------------------

export const OUTBOUND_QUERY_KEY = ['outbound-invoices'] as const;

/** Page size for the outbound list — matches the API default. */
export const OUTBOUND_PAGE_SIZE = 50;

async function fetchOutboundPage(
  offset: number,
  state?: IssuanceState,
): Promise<PagedOutboundInvoices> {
  const params = new URLSearchParams({
    limit: String(OUTBOUND_PAGE_SIZE),
    offset: String(offset),
  });
  if (state) params.set('state', state);
  return request<PagedOutboundInvoices>(`/api/v1/outbound-invoices?${params.toString()}`);
}

// --- Hooks ---------------------------------------------------------------------------------------

/** The outbound invoice list, most-recently-created first, fetched a page at a time (optional state filter). */
export function useOutboundInvoices(state?: IssuanceState) {
  return useInfiniteQuery({
    queryKey: [...OUTBOUND_QUERY_KEY, 'list', state ?? 'ALL'],
    queryFn: ({ pageParam }) => fetchOutboundPage(pageParam, state),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.nextOffset,
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });
}

/**
 * Manual refresh of the outbound register: collapse to page 1, refetch, and pull the sidebar badge counts
 * alongside so the nav pills agree with what the user just pulled by hand. Scoped to the `'list'`
 * sub-key so it never disturbs a row's `'detail'` entry (which polls on its own while in-flight).
 */
export function useRefreshOutbound() {
  return useRefreshList([...OUTBOUND_QUERY_KEY, 'list'], { also: [BADGE_COUNTS_QUERY_KEY] });
}

/**
 * One outbound invoice with its timeline + attempts. Polls every few seconds while the row is still
 * in-flight (the worker/sweep is advancing it), and stops once it settles — so a freshly-issued invoice
 * animates QUEUED → … → COMPLETED without a manual refresh, and a resting row costs nothing.
 */
export function useOutboundInvoice(id: string) {
  return useQuery<OutboundInvoiceDetail>({
    queryKey: [...OUTBOUND_QUERY_KEY, 'detail', id],
    queryFn: () => request<OutboundInvoiceDetail>(`/api/v1/outbound-invoices/${id}`),
    refetchInterval: (query) => {
      const state = query.state.data?.invoice.state;
      return state && isInFlight(state) ? 4000 : false;
    },
    retry: false,
  });
}

/** Invalidates both the list and this row's detail after a lifecycle action so the UI reflects the move. */
function useInvalidateOutbound(id: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: [...OUTBOUND_QUERY_KEY, 'list'] });
    void qc.invalidateQueries({ queryKey: [...OUTBOUND_QUERY_KEY, 'detail', id] });
    void qc.invalidateQueries({ queryKey: BADGE_COUNTS_QUERY_KEY });
  };
}

/** `POST /{id}/retry` — re-queue a NEEDS_ATTENTION invoice (→ QUEUED). 409 if the state raced away. */
export function useRetryOutbound(id: string) {
  const invalidate = useInvalidateOutbound(id);
  return useMutation<void, ApiError, void>({
    mutationFn: () => request<void>(`/api/v1/outbound-invoices/${id}/retry`, { method: 'POST' }),
    onSuccess: invalidate,
  });
}

/** `POST /{id}/abandon` — give up on an invoice (frees its number). 409 from an in-flight state. */
export function useAbandonOutbound(id: string) {
  const invalidate = useInvalidateOutbound(id);
  return useMutation<void, ApiError, void>({
    mutationFn: () => request<void>(`/api/v1/outbound-invoices/${id}/abandon`, { method: 'POST' }),
    onSuccess: invalidate,
  });
}

/**
 * `POST /{id}/resend` — one-click renumber-and-resend of a 440-duplicate rejection. Re-renders from the
 * frozen document with the next free number (→ QUEUED). 409 `NOT_A_DUPLICATE` when the rejection was for
 * another reason (renumbering can't fix a schema/content problem).
 */
export function useResendOutbound(id: string) {
  const invalidate = useInvalidateOutbound(id);
  return useMutation<OutboundResend, ApiError, void>({
    mutationFn: () =>
      request<OutboundResend>(`/api/v1/outbound-invoices/${id}/resend`, { method: 'POST' }),
    onSuccess: invalidate,
  });
}

/**
 * `POST /api/v1/invoice-drafts/{id}/issue` — the fiscal action. Freezes the draft into an outbound
 * invoice and queues it for KSeF. Lives here (not in the draft client) because it creates outbound data;
 * on success it invalidates both the outbound list and the drafts list (the draft flips to ISSUING).
 * Throws `ApiError` with `code` in {NOT_READY_TO_ISSUE, DRAFT_NOT_ISSUABLE, UNACKNOWLEDGED_WARNINGS,
 * DUPLICATE_ISSUE} — the dialog maps each to copy.
 */
export function useIssueDraft() {
  const qc = useQueryClient();
  return useMutation<IssueAccepted, ApiError, { draftId: number; body: IssueRequest }>({
    mutationFn: ({ draftId, body }) =>
      request<IssueAccepted>(`/api/v1/invoice-drafts/${draftId}/issue`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...OUTBOUND_QUERY_KEY, 'list'] });
      void qc.invalidateQueries({ queryKey: [...INVOICE_DRAFTS_QUERY_KEY, 'list'] });
      void qc.invalidateQueries({ queryKey: BADGE_COUNTS_QUERY_KEY });
    },
  });
}
